<template>
  <div class="main-space">
    <LegacyWrap>
      <AppLink class="title" to="/journal/">
        <h2 class="title">{{ title }}</h2>
      </AppLink>
      <div class="subtitle">{{ subtitle }}</div>
    </LegacyWrap>
    <div class="swiper-main-container">
      <UiSlider v-bind="cardProps" :key="sliderKey" arrows-always-visible>
        <template #skeleton>
          <Skeleton />
        </template>
        <template #slide="{ slide, index }">
          <Card
            v-view-tracker="getParams(slide, index)"
            v-click-tracker="getParams(slide, index)"
            :index="index"
            :el="slide"
            :data-index="index"
          />
        </template>
        <template #rightArrowIcon>
          <IconBase name="right-arrow" color="--alias-color-icon-subtle" />
        </template>
        <template #leftArrowIcon>
          <IconBase class="mirror" name="right-arrow" color="--alias-color-icon-subtle" />
        </template>
      </UiSlider>
    </div>

    <LegacyWrap class="center">
      <UiButton
        v-click-btn-tracker
        data-test="button-articles-more"
        class="btn"
        :variant="UiButtonVariant.OUTLINE"
        :color-mode="UiButtonColorMode.NEUTRAL"
        :text="t('viewAll')"
        :is-full-width="isMobileOrTablet"
        href="/journal/"
      />
    </LegacyWrap>
  </div>
</template>

<script setup lang="ts">
import { UiButton, UiButtonColorMode, UiButtonVariant } from '@experience/core-button-ui';
import { UiSlider } from '@experience/core-slider-ui';
import { useI18n } from '@experience/i18n';
import { computed } from 'vue';

import ru from '@/features/journalContainer/i18n/ru';
import Card from '@/features/journalContainer/JournalCard.vue';
import Skeleton from '@/features/journalContainer/SliderCardSkeleton.vue';
import IconBase from '@/shared/icons/IconBase.vue';
import LegacyWrap from '@/shared/LegacyWrap.vue';
import { useAnalytics } from '@/shared/lib/analytics';
import { useCarouselConfig } from '@/shared/lib/carousel/useCarouselConfig';
import type { Article } from '@/shared/models/journal.ts';
import { useScreenSize } from '@/shared/utils/breakpoints';

type MessageSchema = typeof ru;
const { t } = useI18n<{ message: MessageSchema }, 'ru'>({
  inheritLocale: true,
  messages: { ru },
});

const props = defineProps<{
  items: Article[];
  isLoaded: boolean;
  title: string;
  subtitle: string;
}>();

const breakpoints = useScreenSize();

const { useViewTracker, useImmediateClickTracker } = useAnalytics();
const vViewTracker = useViewTracker('main:articles:article.view');
const vClickTracker = useImmediateClickTracker('main:articles:article.click');
const vClickBtnTracker = useImmediateClickTracker('main:articles:show-all-button.click');

const getParams = (slide: Article, index: number) => ({ position: index, article_id: slide.id });

const sliderKey = computed(() => `articles-${String(props.items.length)}-${String(props.isLoaded)}`);

const isMobileOrTablet = computed(() => {
  return breakpoints.isTabletOnly.value || breakpoints.isMobile.value;
});

const cardProps = useCarouselConfig<Article>({
  slides: () => props.items,
  tabletMatcher: 'isTabletOnly',
  mobileMini: {
    cardWidth: 300,
    spaceBetween: 8,
    options: { initialSlide: 0, slidesOffsetBefore: 20, slidesOffsetAfter: 20 },
  },
  tabletMobile: {
    slidesPerView: 2.3,
    spaceBetween: 8,
    options: {
      initialSlide: 0,
      slidesOffsetBefore: 20,
      slidesOffsetAfter: 20,
      breakpoints: { 767: { slidesPerGroup: 2 } },
    },
  },
  desktop: {
    slidesPerView: 3,
    spaceBetween: 24,
    options: { breakpoints: { 1023: { slidesPerGroup: 3 } } },
  },
});
</script>

<style lang="scss" scoped>
.title {
  color: var(--alias-color-text-default);
  text-decoration: none;

  @include typography.base-800-750-extra;

  @include adaptivity.mobile {
    @include typography.base-700-500-extra;
  }
}

.subtitle {
  margin-top: var(--alias-space-12, 12px);

  @include typography.base-500-400-regular;

  @include adaptivity.mobile {
    margin-top: var(--alias-space-8, 8px);

    @include typography.base-400-300-regular;
  }
}

:host {
  --wrap-max-width: 1260px;
  --wrap-min-width: 950px;

  width: 100%;

  @include adaptivity.tablet {
    --wrap-max-width: 100%;
    --wrap-min-width: 100%;
  }
}

.btn {
  margin-top: var(--alias-space-40, 40px);

  @include adaptivity.tablet {
    margin-top: var(--alias-space-32, 32px);
  }

  @include adaptivity.mobile {
    margin-top: var(--alias-space-24, 24px);
  }
}

.swiper-main-container:hover {
  :deep(.btn-prev) {
    opacity: 1;
  }

  :deep(.btn-next) {
    opacity: 1;
  }
}

:deep(.btn-prev) {
  top: 25%;
  transform: translate(-50%, 0);

  @include adaptivity.tablet {
    display: none;
  }
}

:deep(.btn-next) {
  top: 25%;
  transform: translate(50%, 0);

  @include adaptivity.tablet {
    display: none;
  }
}

.mirror {
  transform: scaleX(-1);
}

.center {
  display: flex;
  justify-content: center;
}
</style>
